import React, { createContext, useEffect, useRef } from "react";

import { ALL_CADENCES, CLOCK_CADENCE } from "./clock-cadences";
import { wallClockService } from "./wall-clock-service";

/**
 * Calculates the time until the next wall-clock boundary (e.g. the :05, :10 mark)
 * rather than firing N ms from now, so all tabs and page loads tick in sync.
 */
function msUntilNextBoundary(intervalMs: number): number {
    return intervalMs - (Date.now() % intervalMs);
}

/** Signals to child hooks that a provider is mounted. Used only in dev for boundary enforcement. */
export const WallClockMountedContext = createContext(false);

export function WallClockProvider({ children }: { children: React.ReactNode }) {
    const timeoutsRef = useRef<Map<CLOCK_CADENCE, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        wallClockService.notifyMounted();

        /**
         * Recursive setTimeout instead of setInterval. Each callback re-aligns to the
         * next boundary, so drift never accumulates.
         */
        function scheduleCadence(cadence: CLOCK_CADENCE) {
            const recursiveTimeout = setTimeout(() => {
                wallClockService.tick(cadence);
                scheduleCadence(cadence);
            }, msUntilNextBoundary(cadence));

            timeoutsRef.current.set(cadence, recursiveTimeout);
        }

        function clearAll() {
            timeoutsRef.current.forEach((recursiveTimeout) => clearTimeout(recursiveTimeout));
            timeoutsRef.current.clear();
        }

        /**
         * Browsers throttle (or freeze) timers in hidden tabs, so clocks can be
         * arbitrarily stale on reveal.
         *
         * Tick all cadences immediately to flush stale data, then re-align scheduling.
         */
        function onVisibilityChange() {
            if (document.visibilityState !== "visible") return;
            clearAll();
            ALL_CADENCES.forEach((c) => wallClockService.tick(c));
            ALL_CADENCES.forEach(scheduleCadence);
        }

        // Start scheduling timeouts for all cadences.
        ALL_CADENCES.forEach(scheduleCadence);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            clearAll();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            wallClockService.notifyUnmounted();
        };
    }, []);

    return <WallClockMountedContext.Provider value={true}>{children}</WallClockMountedContext.Provider>;
}
